import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Fade
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CodeIcon from '@mui/icons-material/Code';
import type { SaveStatus } from '../hooks/useItemAutoSave';

type EditorMode = 'visual' | 'json';

type Props = {
  editorMode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  saveStatuses: SaveStatus[];
};

export const BuilderToolbar = ({
  editorMode,
  onModeChange,
  saveStatuses
}: Props) => {
  const handleModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: EditorMode | null
  ) => {
    if (newMode) onModeChange(newMode);
  };

  // Aggregate save statuses
  const aggregatedStatus: SaveStatus = saveStatuses.some(s => s === 'error')
    ? 'error'
    : saveStatuses.some(s => s === 'saving')
      ? 'saving'
      : saveStatuses.some(s => s === 'saved')
        ? 'saved'
        : 'idle';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <ToggleButtonGroup
        value={editorMode}
        exclusive
        onChange={handleModeChange}
        size="small"
      >
        <ToggleButton value="visual">
          <ViewModuleIcon sx={{ mr: 0.5 }} fontSize="small" />
          Visual
        </ToggleButton>
        <ToggleButton value="json">
          <CodeIcon sx={{ mr: 0.5 }} fontSize="small" />
          JSON
        </ToggleButton>
      </ToggleButtonGroup>

      <Fade in={aggregatedStatus === 'saving'} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary'
          }}
        >
          <CloudSyncIcon fontSize="small" />
          <Typography variant="caption">Saving...</Typography>
        </Box>
      </Fade>
      <Fade in={aggregatedStatus === 'saved'} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'success.main'
          }}
        >
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="caption">Saved</Typography>
        </Box>
      </Fade>
      <Fade in={aggregatedStatus === 'error'} unmountOnExit>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'error.main'
          }}
        >
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="caption">Save failed</Typography>
        </Box>
      </Fade>
    </Box>
  );
};
